/** Newly finished runs waiting for the shared board. Local scores and replays remain independent. */

import { courseRevision, timedCourseNames } from "@lache/courses";
import { isExplicitName } from "@lache/relay/names";
import { RECENT_TIMES_SIZE, type RecentTime } from "@lache/relay/recent-times";
import type { Tape } from "@lache/sim";
import { BOARD_SIZE, cleanName, MAX_NAME_LENGTH, type ScoreEntry } from "./scores";

const MAX_PENDING = 40;
const MAX_QUEUE_BYTES = 4 * 1024 * 1024;
const MAX_REQUEST_BYTES = 1024 * 1024;
const MAX_TICKS = 36000;
const MAX_AUTO_RETRIES = 5;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export type SubmitResult = "accepted" | "queued" | "rejected" | "disabled";

interface PendingScore {
  id: string;
  course: string;
  /** Serialized at finish time: later renames or recorder changes cannot change this run. */
  body: string;
}

export interface GlobalScoresOptions {
  storage?: Storage | null;
  fetch?: typeof fetch;
  timeoutMs?: number;
  onlineTarget?: EventTarget | null;
  isOnline?: () => boolean;
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function bytes(text: string): number {
  return new TextEncoder().encode(text).length;
}

/** randomUUID requires HTTPS; getRandomValues also works when a phone uses a LAN HTTP address. */
function newToken(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const data = crypto.getRandomValues(new Uint8Array(16));
  data[6] = (data[6]! & 0x0f) | 0x40;
  data[8] = (data[8]! & 0x3f) | 0x80;
  const hex = [...data].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function validCourse(course: unknown): course is string {
  return typeof course === "string" && /^[a-z][a-z0-9-]{0,63}$/.test(course);
}

function validTape(value: unknown, course: string): value is Tape {
  if (!record(value)) return false;
  if (
    value.version !== 0 ||
    value.course !== course ||
    typeof value.tuningHash !== "string" ||
    value.tuningHash.length > 128 ||
    !Number.isInteger(value.seed) ||
    (value.seed as number) < 0 ||
    (value.seed as number) > 0xffffffff ||
    (value.respawn !== undefined && value.respawn !== 0) ||
    !Array.isArray(value.frames) ||
    value.frames.length === 0 ||
    value.frames.length > MAX_TICKS
  )
    return false;
  let ticks = 0;
  for (const frame of value.frames) {
    if (!Array.isArray(frame) || frame.length !== 8 || !frame.every(Number.isInteger)) return false;
    ticks += frame[0];
    if (frame[0] < 1 || ticks > MAX_TICKS) return false;
    for (let i = 1; i <= 4; i++) if (frame[i] < -127 || frame[i] > 127) return false;
    if (frame[5] < 0 || frame[5] > 255 || frame[6] < 0 || frame[6] > 255) return false;
    if (frame[7] < 0 || frame[7] > 15) return false;
  }
  return true;
}

function validEntry(value: unknown): value is ScoreEntry {
  if (!record(value)) return false;
  return (
    typeof value.name === "string" &&
    value.name.trim().length > 0 &&
    value.name.length <= MAX_NAME_LENGTH &&
    typeof value.time === "number" &&
    Number.isFinite(value.time) &&
    value.time >= 0 &&
    value.time <= MAX_TICKS / 120 &&
    typeof value.falls === "number" &&
    Number.isInteger(value.falls) &&
    value.falls >= 0 &&
    typeof value.date === "number" &&
    Number.isFinite(value.date) &&
    value.date >= 0 &&
    typeof value.tuning === "string" &&
    Array.isArray(value.splits) &&
    value.splits.length <= 256 &&
    value.splits.every((split) => typeof split === "number" && Number.isFinite(split) && split >= 0)
  );
}

function validEntries(value: unknown): value is ScoreEntry[] {
  return Array.isArray(value) && value.length <= BOARD_SIZE && value.every(validEntry);
}

function validDate(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 && value <= 8.64e15;
}

function validRecentTime(value: unknown): value is RecentTime {
  if (!record(value)) return false;
  return (
    typeof value.id === "string" &&
    UUID.test(value.id) &&
    typeof value.course === "string" &&
    timedCourseNames.includes(value.course) &&
    typeof value.name === "string" &&
    value.name.trim().length > 0 &&
    value.name.length <= MAX_NAME_LENGTH &&
    typeof value.time === "number" &&
    Number.isFinite(value.time) &&
    value.time >= 0 &&
    value.time <= MAX_TICKS / 120 &&
    typeof value.falls === "number" &&
    Number.isInteger(value.falls) &&
    value.falls >= 0 &&
    value.falls <= MAX_TICKS &&
    validDate(value.date) &&
    (value.achievedAt === undefined || validDate(value.achievedAt))
  );
}

export class GlobalScores {
  readonly enabled: boolean;
  private readonly baseUrl: string;
  private readonly storage: Storage | null;
  private readonly requestFetch: typeof fetch;
  private readonly timeoutMs: number;
  private readonly onlineTarget: EventTarget | null;
  private readonly isOnline: () => boolean;
  private readonly queuePrefix: string;
  private readonly identityKey: string;
  private readonly identities: Record<string, string> = {};
  private readonly memoryPending = new Map<string, PendingScore>();
  private readonly completed = new Set<string>();
  private readonly controllers = new Set<AbortController>();
  private pending: PendingScore[] = [];
  private flushing: Promise<void> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private autoRetries = 0;
  private disposed = false;
  private readonly outcomes = new Map<string, SubmitResult>();
  private readonly onOnline = (): void => {
    void this.flush();
  };

  constructor(baseUrl: string | null = null, options: GlobalScoresOptions = {}) {
    this.baseUrl = baseUrl?.replace(/\/+$/, "") ?? "";
    this.enabled = /^https?:\/\//.test(this.baseUrl);
    this.storage = options.storage === undefined ? safeStorage() : options.storage;
    this.requestFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.timeoutMs = options.timeoutMs ?? 10000;
    this.onlineTarget =
      options.onlineTarget === undefined
        ? typeof window === "undefined"
          ? null
          : window
        : options.onlineTarget;
    this.isOnline =
      options.isOnline ?? (() => typeof navigator === "undefined" || navigator.onLine !== false);
    this.queuePrefix = `lache.global.pending.v2:${this.baseUrl}:`;
    this.identityKey = `lache.global.players.v1:${this.baseUrl}`;
    if (!this.enabled) return;
    this.syncPending();
    this.onlineTarget?.addEventListener("online", this.onOnline);
    void this.flush();
  }

  get pendingCount(): number {
    if (this.enabled) this.syncPending();
    return this.pending.length;
  }

  /** Read the shared identity at enqueue time, including identities created by another open tab. */
  private playerToken(seat: number): string {
    const shared: Record<string, string> = {};
    try {
      const saved: unknown = JSON.parse(this.storage?.getItem(this.identityKey) ?? "{}");
      if (record(saved)) {
        for (let index = 0; index < 4; index++) {
          const token = saved[String(index)];
          if (typeof token === "string" && UUID.test(token)) shared[String(index)] = token;
        }
      }
    } catch {
      // An unavailable store uses the identity already chosen during this session.
    }
    const key = String(seat);
    const token = shared[key] ?? this.identities[key] ?? newToken();
    this.identities[key] = token;
    if (shared[key] !== token) {
      shared[key] = token;
      try {
        this.storage?.setItem(this.identityKey, JSON.stringify(shared));
      } catch {
        // The token still lasts for this session.
      }
    }
    return token;
  }

  /** Each run has its own key: a stale tab can only remove that run, never replace another tab's queue. */
  private syncPending(): void {
    try {
      const found = new Map<string, PendingScore>();
      const keys: string[] = [];
      for (let index = 0; index < (this.storage?.length ?? 0); index++) {
        const key = this.storage!.key(index);
        if (key?.startsWith(this.queuePrefix)) keys.push(key);
      }
      let totalBytes = 2;
      for (const key of keys) {
        const raw = this.storage!.getItem(key);
        if (!raw || bytes(raw) > MAX_QUEUE_BYTES) continue;
        try {
          const item: unknown = JSON.parse(raw);
          if (
            !record(item) ||
            typeof item.id !== "string" ||
            key !== `${this.queuePrefix}${item.id}` ||
            this.completed.has(item.id) ||
            !validCourse(item.course) ||
            typeof item.body !== "string" ||
            bytes(item.body) > MAX_REQUEST_BYTES
          )
            continue;
          const payload: unknown = JSON.parse(item.body);
          if (
            !record(payload) ||
            typeof payload.playerToken !== "string" ||
            !UUID.test(payload.playerToken) ||
            typeof payload.name !== "string" ||
            payload.name.length > MAX_NAME_LENGTH ||
            !validTape(payload.tape, item.course)
          )
            continue;
          const size = bytes(raw) + 1;
          if (found.size >= MAX_PENDING || totalBytes + size > MAX_QUEUE_BYTES) continue;
          totalBytes += size;
          found.set(item.id, { id: item.id, course: item.course, body: item.body });
        } catch {
          // One damaged run does not discard its neighbors.
        }
      }
      for (const [id, item] of this.memoryPending) found.set(id, item);
      this.pending = [...found.values()];
    } catch {
      // If storage becomes unavailable, retain the last snapshot plus this session's new items.
      const found = new Map(
        this.pending.filter((item) => !this.completed.has(item.id)).map((item) => [item.id, item]),
      );
      for (const [id, item] of this.memoryPending) found.set(id, item);
      this.pending = [...found.values()];
    }
  }

  private keep(item: PendingScore): void {
    this.memoryPending.set(item.id, item);
    try {
      if (this.storage) {
        this.storage.setItem(`${this.queuePrefix}${item.id}`, JSON.stringify(item));
        this.memoryPending.delete(item.id);
      }
    } catch {
      // Full or unavailable storage retains this run in memory for the current session.
    }
    this.syncPending();
  }

  private forget(item: PendingScore): void {
    this.memoryPending.delete(item.id);
    this.completed.add(item.id);
    try {
      this.storage?.removeItem(`${this.queuePrefix}${item.id}`);
    } catch {
      // A removal failure can cause an idempotent retry after reload, never a second score.
    }
  }

  /** Web Locks make identity creation and shared queue bounds atomic across tabs on secure origins. */
  private async enqueue<T>(action: () => T): Promise<T> {
    if (typeof navigator !== "undefined" && navigator.locks) {
      try {
        return await navigator.locks.request(this.queuePrefix, action);
      } catch {
        // Some browser privacy modes block Web Locks; LAN HTTP may omit it altogether.
      }
    }
    return action();
  }

  async submit(
    course: string,
    name: string,
    tape: Tape,
    seat = 0,
    achievedAt = Date.now(),
  ): Promise<SubmitResult> {
    if (!this.enabled || this.disposed) return "disabled";
    if (isExplicitName(name)) return "rejected";
    if (!validCourse(course) || !validTape(tape, course) || !Number.isInteger(seat) || seat < 0 || seat > 3)
      return "rejected";
    const snapshot = JSON.stringify({
      name: cleanName(name),
      tape,
      achievedAt,
      courseRevision: courseRevision(course),
    });
    const item = await this.enqueue(() => {
      if (this.disposed) return null;
      this.syncPending();
      const candidate = {
        id: newToken(),
        course,
        body: JSON.stringify({ playerToken: this.playerToken(seat), ...JSON.parse(snapshot) }),
      };
      if (
        bytes(candidate.body) > MAX_REQUEST_BYTES ||
        this.pending.length >= MAX_PENDING ||
        bytes(JSON.stringify([...this.pending, candidate])) > MAX_QUEUE_BYTES
      )
        return null;
      this.keep(candidate);
      return candidate;
    });
    if (!item) return "rejected";
    this.outcomes.set(item.id, "queued");
    await this.flush();
    const outcome = this.outcomes.get(item.id) ?? "queued";
    this.outcomes.delete(item.id);
    return outcome;
  }

  async load(course: string): Promise<ScoreEntry[]> {
    if (!this.enabled || this.disposed) return [];
    if (!validCourse(course)) throw new Error("Unknown course");
    const response = await this.request(`/leaderboard/${encodeURIComponent(course)}`);
    if (
      response.status < 200 ||
      response.status >= 300 ||
      !record(response.body) ||
      response.body.course !== course ||
      typeof response.body.tuning !== "string" ||
      !validEntries(response.body.entries)
    )
      throw new Error("The global leaderboard is unavailable");
    return response.body.entries.map((entry) => ({ ...entry, name: cleanName(entry.name) }));
  }

  /** Recent verified finishes across the current layouts, independent of best-time ranking. */
  async recentTimes(): Promise<RecentTime[]> {
    if (!this.enabled || this.disposed) return [];
    const response = await this.request("/recent-times");
    const entries = record(response.body) ? response.body.entries : null;
    if (
      response.status < 200 ||
      response.status >= 300 ||
      !Array.isArray(entries) ||
      entries.length > RECENT_TIMES_SIZE ||
      !entries.every(validRecentTime) ||
      new Set(entries.map((entry) => entry.id)).size !== entries.length
    )
      throw new Error("Latest times are unavailable");
    return entries.map((entry) => ({
      id: entry.id,
      course: entry.course,
      name: cleanName(entry.name),
      time: entry.time,
      falls: entry.falls,
      date: entry.date,
      ...(entry.achievedAt === undefined ? {} : { achievedAt: entry.achievedAt }),
    }));
  }

  flush(): Promise<void> {
    this.autoRetries = 0;
    this.clearRetry();
    return this.startFlush();
  }

  private startFlush(): Promise<void> {
    if (!this.enabled || this.disposed) return Promise.resolve();
    if (this.flushing) return this.flushing;
    this.syncPending();
    if (this.pending.length === 0 || !this.isOnline()) return Promise.resolve();
    this.flushing = this.flushPending().finally(() => {
      this.flushing = null;
    });
    return this.flushing;
  }

  private async flushPending(): Promise<void> {
    while (!this.disposed) {
      this.syncPending();
      if (this.pending.length === 0) return;
      const item = this.pending[0]!;
      try {
        const response = await this.request(`/leaderboard/${encodeURIComponent(item.course)}`, item.body);
        const accepted =
          response.status >= 200 &&
          response.status < 300 &&
          record(response.body) &&
          response.body.accepted === true &&
          validEntry(response.body.entry) &&
          validEntries(response.body.entries);
        const rejected =
          response.status >= 400 &&
          response.status < 500 &&
          response.status !== 408 &&
          response.status !== 429;
        if (!accepted && !rejected) {
          this.scheduleRetry(response.retryAfterMs ?? (response.status === 429 ? 3000 : 0));
          return;
        }
        if (this.outcomes.has(item.id)) this.outcomes.set(item.id, accepted ? "accepted" : "rejected");
        this.forget(item);
        // The limit counts consecutive failures, so a long healthy queue survives per-run throttling.
        this.autoRetries = 0;
      } catch {
        this.scheduleRetry();
        return;
      }
    }
  }

  private clearRetry(): void {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private scheduleRetry(retryAfterMs = 0): void {
    this.clearRetry();
    this.syncPending();
    if (
      this.disposed ||
      !this.isOnline() ||
      this.pending.length === 0 ||
      this.autoRetries >= MAX_AUTO_RETRIES
    )
      return;
    const delay = Math.max(retryAfterMs, Math.min(60000, 1000 * 2 ** this.autoRetries));
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (!this.isOnline()) return;
      this.autoRetries++;
      void this.startFlush();
    }, delay);
  }

  private async request(
    path: string,
    body?: string,
  ): Promise<{ status: number; body: unknown; retryAfterMs?: number }> {
    const controller = new AbortController();
    this.controllers.add(controller);
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await this.requestFetch(`${this.baseUrl}${path}`, {
        method: body === undefined ? "GET" : "POST",
        ...(body === undefined ? {} : { body, headers: { "Content-Type": "application/json" } }),
        credentials: "omit",
        signal: controller.signal,
      });
      let parsed: unknown = null;
      if (response.ok) {
        try {
          parsed = await response.json();
        } catch {
          // A truncated response is transient; keep its submission pending.
        }
      }
      const retryAfter = response.headers.get("Retry-After");
      let retryAfterMs: number | undefined;
      if (retryAfter !== null) {
        const seconds = Number(retryAfter);
        const delay = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(retryAfter) - Date.now();
        if (Number.isFinite(delay) && delay >= 0) retryAfterMs = delay;
      }
      return {
        status: response.status,
        body: parsed,
        ...(retryAfterMs === undefined ? {} : { retryAfterMs }),
      };
    } finally {
      clearTimeout(timer);
      this.controllers.delete(controller);
    }
  }

  dispose(): void {
    this.disposed = true;
    this.clearRetry();
    this.onlineTarget?.removeEventListener("online", this.onOnline);
    for (const controller of this.controllers) controller.abort();
  }
}
